import { useState } from 'react';

import { HStack, Heading, Stack, Text, Divider } from '@ecoinvent/ui';

import PageHead from 'components/shared/PageHead';
import AppCard from 'components/UI/AppCard';
import useRecentDatasets from 'hooks/data/datasets/useRecentDatasets';
import { FeaturedType } from 'hooks/data/search/types';
import useFeaturedDatasets from 'hooks/data/search/useFeaturedDatasets';
import { useIsShowingV4 } from 'hooks/useV4';
import FeaturedDatasetCard from 'pages/Landing/components/FeaturedDatasetCard';

import ExploreDatasets from './components/ExploreDatasets';
import GuideCard from './components/GuideCard';
import Hero from './components/Hero';
import { getStartedGuideSteps } from './content';

const Landing = () => {
  const [selectedSubSectors, setSelectedSubSectors] = useState<string[]>([]);

  const { data: recentDatasets = [] } = useRecentDatasets();

  const { data, isLoading } = useFeaturedDatasets({ limit: 9, sector: selectedSubSectors, interval: 'month' });

  const isShowingV4 = useIsShowingV4();

  return (
    <Stack
      width="100%"
      alignSelf={'center'}
      justifyContent="space-between"
      alignItems="center"
      overflow="clip"
      spacing={7}
      maxW="100rem"
    >
      <PageHead title="Home" />
      <Hero />
      {recentDatasets.length > 0 && !isShowingV4 && (
        <AppCard w="100%">
          <Heading size="md">Recently Viewed Datasets</Heading>
          <Stack width={'100%'}>
            <HStack
              alignItems="start"
              display="grid"
              gridTemplateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }}
              gridTemplateRows="auto"
            >
              {recentDatasets?.slice(0, 4).map((featuredDataset) => (
                <FeaturedDatasetCard
                  key={featuredDataset.dataset.index}
                  name={featuredDataset.dataset.activity_name}
                  refProduct={featuredDataset.dataset.reference_product}
                  geography={
                    typeof featuredDataset.dataset.geography === 'string'
                      ? featuredDataset.dataset.geography
                      : `${featuredDataset.dataset.geography.long_name} (${featuredDataset.dataset.geography.short_name})`
                  }
                  spoldId={featuredDataset.dataset.index.toString()}
                  badgeText={FeaturedType.RANDOM}
                  viewedAt={featuredDataset.viewed_at}
                  version={featuredDataset.dataset.version}
                  systemModel={featuredDataset.dataset.system_model}
                  type="recent"
                />
              ))}
            </HStack>
          </Stack>
        </AppCard>
      )}
      {!isShowingV4 ? (
        <ExploreDatasets
          selectedSubSectors={selectedSubSectors}
          setSelectedSubSectors={setSelectedSubSectors}
          isLoading={isLoading}
          data={data}
        />
      ) : null}
      <HStack
        width="100%"
        alignSelf={'center'}
        flexDirection={{ base: 'column', xl: 'row' }}
        spacing={7}
        justifyContent="space-between"
        alignItems="start"
      >
        <Stack
          alignItems={{ base: 'center', xl: 'stretch' }}
          textAlign={{ base: 'center', xl: 'left' }}
          maxW={{ base: 'auto', xl: 80 }}
          px={{ base: 4, md: 0 }}
        >
          <Heading size="lg">Get started with ecoinvent data</Heading>
          <Text>Browse numerous resources to help you understand how to work with ecoinvent datasets, scores, and more.</Text>
        </Stack>
        <AppCard
          w="100%"
          display="grid"
          gridTemplateColumns={{ base: 'repeat(1,1fr)', xl: 'repeat(2, 1fr)' }}
          gridTemplateRows="auto"
          px={{ base: 4, md: 8 }}
          py={5}
          borderRadius="md"
        >
          {getStartedGuideSteps.map((step) => (
            <GuideCard key={step.title} step={step} />
          ))}
        </AppCard>
      </HStack>
      <Divider />
    </Stack>
  );
};

export default Landing;
